import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const ws = useRef(null);
  const { user } = useAuth();
  const reconnectTimeout = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const messageHandlers = useRef(new Set());

  const connect = useCallback(() => {
    if (!user?.id) {
      console.log('⏸️ No user ID, skipping WebSocket connection');
      return;
    }

    // Don't reconnect if already connected or connecting
    if (ws.current) {
      if (ws.current.readyState === WebSocket.OPEN) {
        console.log('✅ WebSocket already connected');
        return;
      }
      if (ws.current.readyState === WebSocket.CONNECTING) {
        console.log('⏳ WebSocket already connecting...');
        return;
      }
    }

    // Limit reconnection attempts
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/ws/${user.id}`;

    console.log(`🔌 [WebSocket] Connecting to: ${wsUrl} (attempt ${reconnectAttempts.current + 1})`);

    try {
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log('✅ [WebSocket] Connected successfully');
        setIsConnected(true);
        reconnectAttempts.current = 0; // Reset counter on successful connection
        
        // Clear any pending reconnect timeout
        if (reconnectTimeout.current) {
          clearTimeout(reconnectTimeout.current);
          reconnectTimeout.current = null;
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // ===== LOGS MUY VISIBLES PARA DEBUGGING =====
          console.log('========================================');
          console.log('✅ [WebSocket] MENSAJE RECIBIDO');
          console.log('📨 Tipo:', data.type);
          console.log('📦 Payload completo:', JSON.stringify(data, null, 2));
          console.log('👥 Handlers registrados:', messageHandlers.current.size);
          console.log('========================================');
          
          // Update lastMessage state
          setLastMessage(data);
          
          // Notify all registered handlers
          let handlerIndex = 0;
          messageHandlers.current.forEach(handler => {
            try {
              handlerIndex++;
              console.log(`🔔 [WebSocket] Ejecutando handler #${handlerIndex}`);
              handler(data);
              console.log(`✅ [WebSocket] Handler #${handlerIndex} ejecutado correctamente`);
            } catch (error) {
              console.error(`❌ [WebSocket] Error en handler #${handlerIndex}:`, error);
            }
          });
          
          console.log('🏁 [WebSocket] Procesamiento de mensaje completado');
        } catch (error) {
          console.error('❌ [WebSocket] Error parsing message:', error);
          console.error('Raw message:', event.data);
        }
      };

      ws.current.onerror = (error) => {
        console.error('❌ [WebSocket] Error:', error);
      };

      ws.current.onclose = (event) => {
        console.log(`❌ [WebSocket] Disconnected (code: ${event.code}, reason: ${event.reason})`);
        setIsConnected(false);
        
        // Only attempt to reconnect if not a normal closure and user is still logged in
        if (event.code !== 1000 && user?.id && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // Exponential backoff, max 30s
          
          console.log(`🔄 [WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})...`);
          
          reconnectTimeout.current = setTimeout(() => {
            connect();
          }, delay);
        }
      };
    } catch (error) {
      console.error('❌ [WebSocket] Connection error:', error);
    }
  }, [user]);

  // Connect when user logs in
  useEffect(() => {
    if (user?.id) {
      connect();
    }

    // Cleanup on unmount or user logout
    return () => {
      console.log('🧹 [WebSocket] Cleaning up connection');
      
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      
      if (ws.current) {
        // Close with normal closure code
        ws.current.close(1000, 'Component unmounting');
        ws.current = null;
      }
      
      reconnectAttempts.current = 0;
    };
  }, [user?.id, connect]);

  // Subscribe to messages
  const subscribe = useCallback((handler) => {
    messageHandlers.current.add(handler);
    
    // Return unsubscribe function
    return () => {
      messageHandlers.current.delete(handler);
    };
  }, []);

  // Send message
  const sendMessage = useCallback((message) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      try {
        ws.current.send(JSON.stringify(message));
        console.log('📤 [WebSocket] Message sent:', message);
      } catch (error) {
        console.error('❌ [WebSocket] Error sending message:', error);
      }
    } else {
      console.warn('⚠️ [WebSocket] Cannot send message - not connected');
    }
  }, []);

  const value = {
    isConnected,
    lastMessage,
    sendMessage,
    subscribe
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within WebSocketProvider');
  }
  return context;
};
